import React, { useContext, useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from "react-router-dom"
import dayjs, { Dayjs } from 'dayjs'
import { Helmet } from "react-helmet"
import {
    Space,
    App,
    Button,
    Row,
    Col,
    Typography,
    Flex,
    Tabs,
    Table,
    Tag,
    Divider,
    Spin,
    Tooltip,
    Upload,
    Modal,
    Alert,
    Affix,
} from 'antd'
import {
    FaChevronRight,
    FaMagic,
    FaCalendarPlus,
    FaCube,
    FaRegClock,
    FaRegUserCircle,
    FaRegFlag,
    FaInfoCircle,
    FaFileDownload,
    FaRegFolderOpen,
    FaArrowCircleRight,
    FaSitemap,
    FaFileAlt,
    FaCloudUploadAlt,
    FaRegCheckCircle,
} from "react-icons/fa"
import { HiOutlinePlay } from "react-icons/hi2";
import { FiAlertTriangle } from "react-icons/fi";
import { useParams } from "react-router-dom"
import _ from 'lodash'
import { saveAs } from "file-saver";
import { API, Enum, ActionType } from "../../../resources/constants"
import language from "../../../resources/languages/en_US"

// Interfaces
import { Tender as TenderType, Ask as AskType, Message as MessageType } from "../../../interfaces/pages/opportunities"
import { AxiosError } from "../../../interfaces/axios";

import type { GetProp, UploadFile, UploadProps } from 'antd';
type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0]

// Contexts
import AxiosContext from "../../../contexts/axios";
import GlobalContext from "../../../contexts/global";

// Components
import { DecisionIntelligence } from "./decision-intelligence-pdf";
import { Proposal } from "./proposal-doc";
import Ask from "./ask";
import AskHistory from "./ask-history";

const { App: { title }, Pages: { Opportunities: { Tender } }, Share, Validation, Message: MessageLanguage, Enum: EnumLanguage } = language
const { TENDER_TENDER_ID, TENDER_TENDER_DOWNLOAD_FILE, TENDER_TENDER_ADD_FILE, TENDER_TENDER_DECISION_INTELLIGENCE, TENDER_CHAT_ALL, TENDER_CHAT_ID, TENDER_TENDER_ADD_CRM } = API;
const { SET_DEFAULT_SOURCE } = ActionType;
const { Message, OverallStatus, Source } = Enum;
const baseURLUSA = process.env.NODE_ENV === 'development' ? process.env.REACT_APP_DEVELOPMENT_API_URL : process.env.REACT_APP_PRODUCTION_API_URL
const baseURLCanada = process.env.NODE_ENV === 'development' ? process.env.REACT_APP_DEVELOPMENT_CANADA_API_URL : process.env.REACT_APP_PRODUCTION_CANADA_API_URL

export default function Index() {

    const { axios } = useContext(AxiosContext)
    const { accessToken, defaultSource, dispatch } = useContext(GlobalContext)
    const [, forceUpdate] = useState({})
    const [baseURL, setBaseURL] = useState<string|undefined>(baseURLUSA)
    const [showAskBox, setShowAskBox] = useState<boolean>(false)
    const [askId, setAskId] = useState<string|null>(null)
    const [tender, setTender] = useState<TenderType>()
    const [asks, setAsks] = useState<AskType[]>([])
    const [messages, setMessages] = useState<MessageType[]>([])
    const [expireDays, setExpireDays] = useState<number|null>(null)
    const [loadingAsk, setLoadingAsk] = useState<boolean>(false)
    const [uploading, setUploading] = useState(false)
    const [pdfDecisionIntelligenceLoading, setPdfDecisionIntelligenceLoading] = useState(false)
    const [pdfProposalLoading, setPdfProposalLoading] = useState(false)
    const [addCRMLoading, setAddCRMLoading] = useState(false)
    const [loadingMessages, setLoadingMessages] = useState<boolean>(false)
    const [askHistorySearchReset, setAskHistorySearchReset] = useState<boolean>(false)
    const { id: tenderId } = useParams()
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()
    const { message, modal } = App.useApp()
    const source = searchParams.get('source') || defaultSource

    useEffect(() => {
        dispatch({ type: SET_DEFAULT_SOURCE, payload: { defaultSource: source  }});
    }, [source])

    useEffect(() => {
        askId ? getMessages(askId) : setMessages([]);
    }, [askId])

    useEffect(() => {
        // To disable submit button at the beginning.
        forceUpdate({})
        initialAPI().then(response => response)
    }, [])

    const initialAPI = async () => {
        if (tenderId) {
            await getTender(tenderId);
            await getAsks(tenderId);
        }
        // @ts-ignore
        if ([Source.Canada_Tenders, Source.Canada_Grants].includes(source)) setBaseURL(baseURLCanada);
        else setBaseURL(baseURLUSA);
    }

    const getTender = async (tenderId: string): Promise<TenderType> => {
        return axios.get(TENDER_TENDER_ID + tenderId, { params: { source, description: Tender.loadingTender } }).then((response: { data: TenderType }) => {
            const { data: tender } = response;
            setTender(tender)
            if (tender.dueDate) {
                const days = dayjs(tender.dueDate).tz(process.env.REACT_APP_TZ).diff(new Date(), 'day')
                setExpireDays(days > 0 ? days : 0)
            }
            return tender;
        }).catch((error: AxiosError) => {
            const { response: { data } } = error;
            // @ts-ignore
            message.error(MessageLanguage[data.message]||data.message).then(response => response )
        })
    }

    const getAsks = async (tenderId: string): Promise<AskType[]> => {
        return axios.post(TENDER_CHAT_ALL, { tender: tenderId }, { params: { source } }).then((response: { data: AskType[] }) => {
            const { data: asks } = response;
            setAsks(asks);
            return asks;
        }).catch((error: AxiosError) => {
            const { response: { data } } = error;
            setAsks([]);
            // @ts-ignore
            message.error(MessageLanguage[data.message]||data.message).then(response => response )
        })
    }

    const getMessages = async (askId: string): Promise<MessageType[]> => {
        setLoadingMessages(true)
        return axios.get(TENDER_CHAT_ID + askId, { params: { source, loading: false } }).then((response: { data: AskType }) => {
            const { data: { messages = [] } } = response;
            setMessages(messages);
            setLoadingMessages(false);
            return messages;
        }).catch((error: AxiosError) => {
            const { response: { data } } = error;
            setMessages([]);
            setLoadingMessages(false);
            // @ts-ignore
            message.error(MessageLanguage[data.message]||data.message).then(response => response )
        })
    }

    const decisionIntelligence = async (tenderId: string): Promise<TenderType> => {
        return axios.get(TENDER_TENDER_DECISION_INTELLIGENCE + tenderId, { params: { source, description: Tender.loadingTender } }).then(() => getTender(tenderId)).catch((error: AxiosError) => {
            const { response: { data } } = error;
            // @ts-ignore
            message.error(MessageLanguage[data.message]||data.message).then(response => response )
        })
    }

    const addCRM = async (tenderId: string): Promise<void> => {
        setAddCRMLoading(true)
        return axios.get(TENDER_TENDER_ADD_CRM + tenderId, { params: { source, loading: false } }).then((response: { data: { message: string } }) => {
            const { data: { message: n8nMessage } } = response;
            setAddCRMLoading(false);
            message.success(n8nMessage).then(response => response);
        }).catch((error: AxiosError) => {
            const { response: { data } } = error;
            setAddCRMLoading(false);
            // @ts-ignore
            message.error(MessageLanguage[data.message]||data.message).then(response => response )
        })
    }

    const beforeUpload = (file: FileType) => {
        const isTxtOrPdfOrDoc = file.type === 'text/plain' || file.type === 'application/pdf' || file.type === 'application/msword' || file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        if (!isTxtOrPdfOrDoc) {
            // @ts-ignore
            message.error(Tender.fileFormat).then(response => response );
        }
        const isLt5M = file.size / 1024 / 1024 < 5;
        if (!isLt5M) {
            // @ts-ignore
            message.error(Tender.fileSize).then(response => response );
        }
        return isTxtOrPdfOrDoc && isLt5M;
    };

    const onChange = async (info: { file: UploadFile, fileList: UploadFile[] }) => {
        const { file, fileList } = info
        const { status } = file;
        if (status === 'uploading') {
            setUploading(true)
        } else if (status === 'done') {
            tenderId && await getTender(tenderId)
            setUploading(false)
            message.success(MessageLanguage[Message.UPLOAD_SUCCESS]).then(response => response)
        } else if (status === 'error') {
            const { response } = file;
            setUploading(false)
            // @ts-ignore
            message.error(response?.message ? response.message : MessageLanguage[Message.UPLOAD_ERROR]).then(response => response)
        }
    };

    return (
        <div className={'tender'}>
            <Helmet>
                {tender?.title ? <title>{title} - {tender?.title}</title> : <title>{title}</title>}
            </Helmet>
            <Space direction={'vertical'} size={30} style={{width:'100%'}}>
                <Space className={'buttons-left'} style={{width:'100%'}}>
                    <Button
                        type={'text'}
                        icon={<FaChevronRight size={16} />}
                        className={'button-grey'}
                        onClick={async () => {
                            navigate({
                                pathname: '/opportunities',
                                search: '?source=' + tender?.type,
                            })
                        }}>
                        {
                            // @ts-ignore
                            tender?.type && EnumLanguage.Source[tender.type]
                        }
                    </Button>
                    <Button type={'primary'} icon={<FaChevronRight size={16} />} onClick={() => tenderId && getTender(tenderId)} >{tender?.title}</Button>
                </Space>
                <Space className={'buttons-right'} style={{width:'100%'}}>
                    <Button type={'text'} icon={<FaMagic size={16} />} className={'button-black shine'} onClick={() => {
                        setShowAskBox(true);
                        setAskId(null);
                    }}>
                        {Tender.askAlpha}
                    </Button>
                    <Button loading={addCRMLoading} type={'primary'} icon={<FaCalendarPlus size={16} />} onClick={() => tenderId && addCRM(tenderId)}>
                        {Tender.addToCRM}
                    </Button>
                    <Button type={'text'} icon={<HiOutlinePlay  size={20} />} className={'button-green '} onClick={() => tenderId && decisionIntelligence(tenderId)}>
                        {Tender.runIntelligence}
                    </Button>
                </Space>
                <Row gutter={20}>
                    <Col xs={24} sm={24} lg={24} xl={4}>
                        <Affix offsetTop={10}>
                            <div className={'ask-history'}>
                                {tenderId &&
                                    <AskHistory source={source} tenderId={tenderId} askId={askId} asks={asks} loadingAsk={loadingAsk} loadingMessages={loadingMessages} askHistorySearchReset={askHistorySearchReset} setAskId={setAskId}
                                                setShowAskBox={setShowAskBox} setAsks={setAsks}/>}
                            </div>
                        </Affix>
                    </Col>
                    <Col xs={24} sm={24} lg={24} xl={showAskBox ? 16 : 20}>
                        <div className={'tender-details'}>
                            <Flex gap={'middle'} justify={'space-between'} style={{marginTop:20,marginBottom:30}}>
                                <Space align={'baseline'}>
                                    <Typography.Title level={1} style={{marginRight:50}}>{tender?.title}</Typography.Title>
                                    {
                                        expireDays ? (
                                            <>
                                                <Button type={'text'} size={'large'} className={'button-green'}>{Tender.active}</Button>
                                                <Button type={'text'} size={'large'} className={'button-grey'}>{expireDays} {Tender.days}</Button>
                                            </>
                                        ) : expireDays !== null ? (
                                            <Button type={'text'} size={'large'} className={'button-grey'}>{Tender.expired}</Button>
                                        ) : null
                                    }
                                </Space>
                                <Space align={'baseline'} size={20}>
                                    <Typography.Title level={2}>{Tender.solicitationNumber}: {tender?.solicitationNumber}</Typography.Title>
                                    <Button type={'text'} disabled={_.isEmpty(tender?.eligibilityTable) && _.isEmpty(tender?.recommendations)} loading={pdfDecisionIntelligenceLoading} className={'button-white'} icon={<FaArrowCircleRight size={16} />} onClick={async () => {
                                        if (tender) {
                                            setPdfDecisionIntelligenceLoading(true);
                                            if (_.isEmpty(tender.snapshotTable)) await DecisionIntelligence(await decisionIntelligence(tender.id));
                                            else await DecisionIntelligence(tender);
                                            setPdfDecisionIntelligenceLoading(false);
                                        }
                                    }}>{Tender.exportPDF}</Button>
                                </Space>
                            </Flex>
                            <Tabs defaultActiveKey="overview" style={{marginBottom:30}}>
                                <Tabs.TabPane key="overview" tab={Tender.overview} icon={<FaCube />}>
                                    <Space direction={'vertical'} size={2}>
                                        <Typography.Title
                                            level={3}>{Tender.projectScopeWorkService}</Typography.Title>
                                        <Space key={'tag'}>
                                            <Typography.Paragraph>
                                                <Tag
                                                    color="#E8ECEF">{Tender.publishDate}: {tender?.publishDate && dayjs(tender?.publishDate).tz(process.env.REACT_APP_TZ).format('MMMM D, YYYY')}</Tag>
                                                <Tag
                                                    color="#E8ECEF">{Tender.dueDate} {tender?.dueDate && dayjs(tender?.dueDate).tz(process.env.REACT_APP_TZ).format('MMMM D, YYYY') + ' RM EST Time ' + dayjs(tender?.dueDate).tz(process.env.REACT_APP_TZ).format('hh:mm')}</Tag>
                                                {
                                                    // @ts-ignore
                                                    [Source.Canada_Tenders, Source.Canada_Grants].includes(source) ? (
                                                        <>
                                                            <Tag color="#E8ECEF">{Tender.gsin}: {_.isArray(tender?.gsinCode) ? tender?.gsinCode?.join(", ")?.toLowerCase() : tender?.gsinCode?.toLowerCase()}</Tag>
                                                            <Tag color="#E8ECEF">{Tender.unspsc}: {_.isArray(tender?.unspscCode) ? tender?.unspscCode?.join(", ")?.toLowerCase() : tender?.unspscCode?.toLowerCase()}</Tag>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Tag color="#E8ECEF">{Tender.psc}: {_.isArray(tender?.pscCode) ? tender?.pscCode?.join(", ")?.toLowerCase() : tender?.pscCode?.toLowerCase()}</Tag>
                                                        </>
                                                    )
                                                }

                                                <Tag
                                                    color="#E8ECEF">{Tender.naics}: {tender?.naicsCode?.toLowerCase()}</Tag>
                                                <Tooltip key={'notice-type'} placement="top"
                                                         title={`${Tender.noticeType}: ${_.isArray(tender?.noticeType) ? tender?.noticeType?.join(", ")?.toLowerCase() : tender?.noticeType?.toLowerCase()}`}><Tag
                                                    color="#E8ECEF"><span
                                                    className={'ellipsis'}>{Tender.noticeType}: {_.isArray(tender?.noticeType) ? tender?.noticeType?.join(", ")?.toLowerCase() : tender?.noticeType?.toLowerCase()}</span></Tag></Tooltip>
                                            </Typography.Paragraph>
                                        </Space>
                                        <Typography.Title level={3}>{Tender.description}</Typography.Title>
                                        <div
                                            className={'description'}
                                            dangerouslySetInnerHTML={{
                                                __html: tender?.description || '',
                                            }}/>
                                        {
                                            tender?.detailedView?.overview && (
                                                <>
                                                    <p>{tender?.detailedView?.overview?.project_scope}</p>
                                                    <Typography.Title
                                                        level={3}>{Tender.billOfMaterials}</Typography.Title>
                                                    <Table
                                                        className={'ant-table-details'}
                                                        columns={[
                                                            {
                                                                title: Tender.item,
                                                                dataIndex: 'item',
                                                                key: 'item',
                                                            },
                                                            {
                                                                title: Tender.partNumber,
                                                                dataIndex: 'part_number',
                                                                key: 'part_number',
                                                            },
                                                            {
                                                                title: Tender.modelNumber,
                                                                dataIndex: 'model_number',
                                                                key: 'model_number',
                                                            },
                                                            {
                                                                title: Tender.quantity,
                                                                dataIndex: 'quantity',
                                                                key: 'quantity',
                                                            },
                                                            {
                                                                title: Tender.preferredVendor,
                                                                dataIndex: 'preferred_vendor',
                                                                key: 'preferred_vendor',
                                                            },
                                                            {
                                                                title: Tender.cageCode,
                                                                dataIndex: 'cage_code',
                                                                key: 'cage_code',
                                                            },
                                                            {
                                                                title: Tender.technicalSpecification,
                                                                dataIndex: 'technical_specification',
                                                                key: 'technical_specification',
                                                            }
                                                        ]}
                                                        dataSource={tender?.detailedView?.overview?.bill_of_materials}
                                                        pagination={false}
                                                        scroll={{x: true}}
                                                    />
                                                    <Typography.Title
                                                        level={3}>{Tender.vendorSelectionCriteria}</Typography.Title>
                                                    <p>
                                                        <b>{Tender.soleSource}:</b> {tender?.detailedView?.overview?.vendor_selection_criteria?.sole_source}
                                                        <br/>
                                                        <b>{Tender.alternateVendor}:</b> {tender?.detailedView?.overview?.vendor_selection_criteria?.preferred_vendor}
                                                    </p>
                                                    <Typography.Title
                                                        level={3}>{Tender.additionalTechnicalInformation}</Typography.Title>
                                                    <p>{tender?.detailedView?.overview?.additional_technical_information}</p>
                                                </>
                                            )
                                        }
                                    </Space>
                                </Tabs.TabPane>
                                <Tabs.TabPane key="delivery" tab={Tender.delivery} icon={<FaRegClock/>}
                                              disabled={!tender?.detailedView?.delivery_inspection_test}>
                                    {
                                        tender?.detailedView?.delivery_inspection_test && (
                                            <Space direction={'vertical'} size={2}>
                                                <Typography.Title level={3}>{Tender.deliveryAddress}</Typography.Title>
                                                <p>{tender?.detailedView?.delivery_inspection_test?.delivery_address}</p>
                                                <Typography.Title level={3}>{Tender.placePerformance}</Typography.Title>
                                                <p>{tender?.detailedView?.delivery_inspection_test?.place_of_performance}</p>
                                            </Space>
                                        )
                                    }
                                </Tabs.TabPane>
                                <Tabs.TabPane key="buyer" tab={Tender.buyer} icon={<FaRegUserCircle />} disabled={!tender?.detailedView?.buyer}>
                                    {
                                        tender?.detailedView?.buyer && (
                                            <Space direction={'vertical'} size={2}>
                                                <Typography.Title level={3}>{Tender.nameAgency}</Typography.Title>
                                                <p>{tender?.detailedView?.buyer?.agency_name}</p>
                                                <Typography.Title level={3}>{Tender.subTierName}</Typography.Title>
                                                <p>{tender?.detailedView?.buyer?.sub_tier_name}</p>
                                                <Typography.Title level={3}>{Tender.officeName}</Typography.Title>
                                                <p>{tender?.detailedView?.buyer?.office_name}</p>
                                                <Typography.Title level={3}>{Tender.contactInformation}</Typography.Title>
                                                <p>{tender?.detailedView?.buyer?.contact_information?.map(({ email, name, phone }) => (
                                                    <ul>
                                                        <li><b>{Tender.name}: </b><span>{name}</span></li>
                                                        <li><b>{Tender.email}: </b><span>{email}</span></li>
                                                        <li><b>{Tender.phone}: </b><span>{phone}</span></li>
                                                    </ul>
                                                ))}</p>
                                                <Typography.Title level={3}>{Tender.buyerWebsite}</Typography.Title>
                                                <p>{tender?.detailedView?.buyer?.buyer_website}</p>
                                            </Space>
                                        )
                                    }
                                </Tabs.TabPane>
                                <Tabs.TabPane key="inspectionTest" tab={Tender.inspectionTest} icon={<FaRegFlag />} disabled={!tender?.detailedView?.delivery_inspection_test}>
                                    {
                                        tender?.detailedView?.delivery_inspection_test && (
                                            <Space direction={'vertical'} size={2}>
                                                <Typography.Title level={3}>{Tender.specificTestsRequested}</Typography.Title>
                                                <p>{tender?.detailedView?.delivery_inspection_test?.specific_tests_requested}</p>
                                                <Typography.Title level={3}>{Tender.inspectionsRequested}</Typography.Title>
                                                <p>{tender?.detailedView?.delivery_inspection_test?.inspections_requested}</p>
                                                <Typography.Title level={3}>{Tender.additionalRequiredSteps}</Typography.Title>
                                                <p>{tender?.detailedView?.delivery_inspection_test?.additional_required_steps}</p>
                                            </Space>
                                        )
                                    }
                                </Tabs.TabPane>
                                <Tabs.TabPane key="compliance" tab={Tender.compliance} icon={<FaInfoCircle />} disabled={!tender?.detailedView?.compliance}>
                                    {
                                        tender?.detailedView?.compliance && (
                                            <Space direction={'vertical'} size={10}>
                                                <Typography.Title level={3}>{Tender.setAsideInformation}</Typography.Title>
                                                { tender?.detailedView?.compliance?.set_aside_information && <Tag color="#E8ECEF">{tender?.detailedView?.compliance?.set_aside_information}</Tag> }
                                                <Typography.Title level={3}>{Tender.requiredCertificates}</Typography.Title>
                                                <ul>
                                                    {tender?.detailedView?.compliance?.required_certificates?.map((item) => <li>{item}</li>)}
                                                </ul>
                                                <Typography.Title level={3}>{Tender.warrantyDetails}</Typography.Title>
                                                <p>{tender?.detailedView?.compliance?.warranty_details}</p>
                                                <Typography.Title level={3}>{Tender.guaranteeDetails}</Typography.Title>
                                                <p>{tender?.detailedView?.compliance?.guarantee_information}</p>
                                                <Typography.Title level={3}>{Tender.legalClauses}</Typography.Title>
                                                <Table
                                                    className={'ant-table-details'}
                                                    columns={[
                                                        {
                                                            title: Tender.clauseNumber,
                                                            dataIndex: 'clause_number',
                                                            key: 'clause_number',
                                                        },
                                                        {
                                                            title: Tender.clauseTitle,
                                                            dataIndex: 'clause_title',
                                                            key: 'clause_title',
                                                        },
                                                        {
                                                            title: Tender.description,
                                                            dataIndex: 'description',
                                                            key: 'description',
                                                        }
                                                    ]}
                                                    dataSource={tender?.detailedView?.compliance?.legal_clauses}
                                                    pagination={false}
                                                    scroll={{ x: true }}
                                                />
                                            </Space>
                                        )
                                    }
                                </Tabs.TabPane>
                                {/*<Tabs.TabPane key="commercialInsight" tab={Tender.commercialInsight} icon={<FaSuitcase />} disabled={!tender?.detailedView?.commercial_insight}>*/}
                                {/*    {*/}
                                {/*        tender?.detailedView?.commercial_insight && (*/}
                                {/*            <Space direction={'vertical'} size={2}>*/}
                                {/*                <Typography.Title level={3}>{Tender.estimatedPrice}</Typography.Title>*/}
                                {/*                {*/}
                                {/*                    tender?.detailedView?.commercial_insight?.whole_estimated_price && (*/}
                                {/*                        <p>{Tender.estimatedPricePartOne} ${tender?.detailedView?.commercial_insight?.whole_estimated_price?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')} {Tender.estimatedPricePartTwo}</p>*/}
                                {/*                    )*/}
                                {/*                }*/}

                                {/*            </Space>*/}
                                {/*        )*/}
                                {/*    }*/}
                                {/*</Tabs.TabPane>*/}
                                <Tabs.TabPane key="docs" tab={Tender.docs} icon={<FaRegFolderOpen />} disabled={!tender?.detailedView?.documents}>
                                    {
                                        tender?.detailedView?.documents && (
                                            <Space direction={'vertical'} size={2}>
                                                <Typography.Title level={3}>{Tender.tenderFiles}</Typography.Title>
                                                <Table
                                                    style={{marginBottom:30}}
                                                    columns={[
                                                        {
                                                            title: Tender.documentName,
                                                            dataIndex: 'name',
                                                            key: 'name',
                                                        },
                                                        {
                                                            title: Tender.documentSize,
                                                            dataIndex: 'size',
                                                            key: 'size',
                                                            render: (size = 0, record) => !_.isNull(size) ? size>1024*1024 ? `${(size/1024/1024).toFixed(1)} MB` : `${(size/1024).toFixed(0)} KB` : ''
                                                        },
                                                        {
                                                            title: Tender.documentDownload,
                                                            dataIndex: 'download',
                                                            key: 'download',
                                                            className: 'td-center',
                                                            render: (text, { size, name, download }) => (
                                                                <Typography.Link disabled={!download}><FaFileDownload className={'download'} size={25} onClick={async () => {
                                                                    if (!_.isNull(size)) {
                                                                        await axios.post(TENDER_TENDER_DOWNLOAD_FILE, { tenderId: tender?.id, fileName: name }, {  responseType: 'blob', params: { source } }).then((response: any) => {
                                                                            const { data } = response;
                                                                            const blob = new Blob([data], {
                                                                                type: response.headers['content-type'],
                                                                            });
                                                                            saveAs(blob, name);
                                                                        }, async (error: AxiosError) => {
                                                                            message.error(MessageLanguage[Message.File_NOT_FOUND]).then(response => response)
                                                                        })
                                                                    } else {
                                                                        const element = document.getElementById('pending-request')
                                                                        if (_.isEmpty(tender.snapshotTable)) {
                                                                            const reloadTender = await decisionIntelligence(tender.id);
                                                                            if (element) element.style.display = "flex";
                                                                            await DecisionIntelligence(reloadTender);
                                                                        } else {
                                                                            if (element) element.style.display = "flex";
                                                                            await DecisionIntelligence(tender);
                                                                        }
                                                                        if (element) element.style.display = "none";
                                                                    }
                                                                }} /></Typography.Link>
                                                            )
                                                        },
                                                    ]}
                                                    dataSource={(_.isEmpty(tender?.eligibilityTable) && _.isEmpty(tender?.recommendations)) ? (tender?.files || []) : _.union(tender?.files || [], [{download: true, name: `${tender.solicitationNumber }.pdf`, size: null}])}
                                                    pagination={false}
                                                    scroll={{ x: true }}
                                                />
                                                <Spin spinning={uploading} tip={Share.loading}>
                                                    <div className={'upload-wrapper'}>
                                                        <Upload.Dragger
                                                            action={baseURL + TENDER_TENDER_ADD_FILE + tender?.id}
                                                            headers={{ Authorization: `Bearer ${accessToken}` }}
                                                            multiple={false}
                                                            showUploadList={false}
                                                            disabled={uploading}
                                                            beforeUpload={beforeUpload}
                                                            onChange={onChange}
                                                        >
                                                            <div className="ant-upload-drag-icon"><FaCloudUploadAlt size={30} /></div>
                                                            <Typography.Title level={3} className="ant-upload-text">{Tender.upload}</Typography.Title>
                                                            <Typography.Title level={4} className="ant-upload-hint">{Tender.uploadDescription}</Typography.Title>
                                                        </Upload.Dragger>
                                                    </div>
                                                </Spin>
                                                <Typography.Title
                                                    level={3}>{Tender.bidSubmissionFormat}</Typography.Title>
                                                <Table
                                                    className={'ant-table-details'}
                                                    columns={[
                                                        {
                                                            title: Tender.requirement,
                                                            dataIndex: 'requirement',
                                                            key: 'requirement',
                                                        },
                                                        {
                                                            title: Tender.description,
                                                            dataIndex: 'description',
                                                            key: 'description',
                                                        }
                                                    ]}
                                                    dataSource={tender?.detailedView?.documents?.bid_submission_format}
                                                    pagination={false}
                                                    scroll={{x: true}}
                                                />
                                            </Space>
                                        )
                                    }
                                </Tabs.TabPane>
                                <Tabs.TabPane key="decisionIntelligence" tab={Tender.decisionIntelligence} icon={<FaSitemap />}>
                                    {
                                        (_.isEmpty(tender?.eligibilityTable) && _.isEmpty(tender?.recommendations)) ?
                                            <>
                                                <Alert
                                                    message={Tender.missingDocumentTitle}
                                                    description={Tender.missingDocumentDecisionIntelligenceDescription}
                                                    type="info"
                                                    showIcon
                                                />
                                            </> :
                                        <>
                                            <Space direction={'vertical'} size={2}>
                                            <Typography.Title level={3}>{Tender.eligibilityReview}</Typography.Title>
                                            <Table
                                                className={'ant-table-details'}
                                                columns={[
                                                    {
                                                        title: Tender.requirement,
                                                        dataIndex: 'requirement',
                                                        key: 'requirement',
                                                    },
                                                    {
                                                        title: Tender.userStatus,
                                                        dataIndex: 'userStatus',
                                                        key: 'userStatus',
                                                        render: (text, record) => text === OverallStatus.Met ? <Tag icon={<FaRegCheckCircle size={16} color={'#0bda1c'} />} className={'ant-tag-user-status'}>{text}</Tag> : text === OverallStatus.Further_Investigation_Required ? <Tag icon={<FiAlertTriangle size={16} color={'#d8741d'} />} className={'ant-tag-user-status'}>{text}</Tag> : <Tag className={'ant-tag-user-status'}>{text}</Tag>
                                                    },
                                                    {
                                                        title: Tender.reference,
                                                        dataIndex: 'reference',
                                                        key: 'reference',
                                                    },

                                                ]}
                                                dataSource={tender?.eligibilityTable}
                                                pagination={false}
                                                scroll={{x: true}}
                                            />
                                            <Alert
                                                message={Tender.overallStatus}
                                                description={<b>{tender?.overallStatus}</b>}
                                                type="info"
                                                showIcon
                                                style={{marginBottom:15}}
                                            />
                                            <Typography.Title level={3}>{Tender.recommendations}</Typography.Title>
                                            <ul>
                                                {
                                                    tender?.recommendations?.map((recommendation: string) => <li>{recommendation}</li>)
                                                }
                                            </ul>

                                            {/*<Typography.Title level={3}>{Tender.strategicAlignment}</Typography.Title>*/}
                                            {/*{*/}
                                            {/*    tender?.decisionIntelligence?.decision_intelligence?.strategic_alignment && (*/}
                                            {/*        <ul>*/}
                                            {/*            <li><b>{Tender.relevanceExpertise}:</b> <span>{tender?.decisionIntelligence?.decision_intelligence?.strategic_alignment?.relevance_of_expertise}</span></li>*/}
                                            {/*            <li><b>{Tender.valueProposition}:</b> <span>{tender?.decisionIntelligence?.decision_intelligence?.strategic_alignment?.value_proposition}</span></li>*/}
                                            {/*            <li><b>{Tender.outcomeAdvantage}:</b> <span>{tender?.decisionIntelligence?.decision_intelligence?.strategic_alignment?.outcome_advantage}</span></li>*/}
                                            {/*        </ul>*/}
                                            {/*    )*/}
                                            {/*}*/}
                                            {/*<Divider />*/}
                                            {/*<Typography.Title level={3}>{Tender.opportunityPrioritization}</Typography.Title>*/}
                                            {/*{*/}
                                            {/*    tender?.decisionIntelligence?.decision_intelligence?.opportunity_prioritization && (*/}
                                            {/*        <ul>*/}
                                            {/*            <li><b>{Tender.setAsideOpportunity}:</b> <span>{tender?.decisionIntelligence?.decision_intelligence?.opportunity_prioritization?.competitive_advantage}</span></li>*/}
                                            {/*            <li><b>{Tender.evaluationFocus}:</b> <span>{tender?.decisionIntelligence?.decision_intelligence?.opportunity_prioritization?.relevance_to_capabilities}</span></li>*/}
                                            {/*            <li><b>{Tender.timelineSubmission}:</b> <span>{tender?.decisionIntelligence?.decision_intelligence?.opportunity_prioritization?.timeline_highlights}</span></li>*/}
                                            {/*        </ul>*/}
                                            {/*    )*/}
                                            {/*}*/}
                                            {/*<Divider />*/}
                                            {/*<Typography.Title level={3}>{Tender.riskMitigation}</Typography.Title>*/}
                                            {/*{*/}
                                            {/*    tender?.decisionIntelligence?.decision_intelligence?.risk_mitigation && (*/}
                                            {/*        <ul>*/}
                                            {/*            <li><b>{Tender.complianceRisks}:</b></li>*/}
                                            {/*            <ul style={{paddingLeft:10,marginBottom:15}}>*/}
                                            {/*                { tender?.decisionIntelligence?.decision_intelligence?.risk_mitigation?.compliance_risks?.map((item) => (<li>{item}</li>))}*/}
                                            {/*            </ul>*/}
                                            {/*            <li><b>{Tender.deliveryRisks}:</b></li>*/}
                                            {/*            <ul style={{paddingLeft:10,marginBottom:15}}>*/}
                                            {/*                { tender?.decisionIntelligence?.decision_intelligence?.risk_mitigation?.delivery_risks?.map((item) => (<li>{item}</li>))}*/}
                                            {/*            </ul>*/}
                                            {/*            <li><b>{Tender.financialRisks}:</b></li>*/}
                                            {/*            <ul style={{paddingLeft:10,marginBottom:15}}>*/}
                                            {/*                { tender?.decisionIntelligence?.decision_intelligence?.risk_mitigation?.financial_risks?.map((item) => (<li>{item}</li>))}*/}
                                            {/*            </ul>*/}
                                            {/*        </ul>*/}
                                            {/*    )*/}
                                            {/*}*/}
                                            {/*<Divider />*/}
                                            {/*<Typography.Title level={3}>{Tender.complianceCertification}</Typography.Title>*/}
                                            {/*{*/}
                                            {/*    tender?.decisionIntelligence?.decision_intelligence?.compliance_certification && (*/}
                                            {/*        <ul>*/}
                                            {/*            <li><b>{Tender.certifications}:</b></li>*/}
                                            {/*            <ul style={{paddingLeft:10,marginBottom:15}}>*/}
                                            {/*                { tender?.decisionIntelligence?.decision_intelligence?.compliance_certification?.certifications_needed?.map((item) => (<li>{item}</li>))}*/}
                                            {/*            </ul>*/}
                                            {/*            <li><b>{Tender.documentation}:</b></li>*/}
                                            {/*            <ul style={{paddingLeft:10,marginBottom:15}}>*/}
                                            {/*                { tender?.decisionIntelligence?.decision_intelligence?.compliance_certification?.documentation_checklist?.map((item) => (<li>{item}</li>))}*/}
                                            {/*            </ul>*/}
                                            {/*            <li><b>{Tender.regulatory}:</b></li>*/}
                                            {/*            <ul style={{paddingLeft:10,marginBottom:15}}>*/}
                                            {/*                { tender?.decisionIntelligence?.decision_intelligence?.compliance_certification?.regulatory_requirements?.map((item) => (<li>{item}</li>))}*/}
                                            {/*            </ul>*/}
                                            {/*        </ul>*/}
                                            {/*    )*/}
                                            {/*}*/}
                                            {/*<Divider />*/}
                                            {/*<Typography.Title level={3}>{Tender.competitivePositioning}</Typography.Title>*/}
                                            {/*{*/}
                                            {/*    tender?.decisionIntelligence?.decision_intelligence?.competitive_positioning && (*/}
                                            {/*        <ul>*/}
                                            {/*            <li><b>{Tender.keyDifferentiators}:</b></li>*/}
                                            {/*            <ul style={{paddingLeft:10,marginBottom:15}}>*/}
                                            {/*                { tender?.decisionIntelligence?.decision_intelligence?.competitive_positioning?.key_differentiators?.map((item) => (<li>{item}</li>))}*/}
                                            {/*            </ul>*/}
                                            {/*            <li><b>{Tender.smallBusinessAdvantage}:</b> <span>{tender?.decisionIntelligence?.decision_intelligence?.competitive_positioning?.emphasized_strengths}</span></li>*/}
                                            {/*        </ul>*/}
                                            {/*    )*/}
                                            {/*}*/}
                                            {/*<Divider />*/}
                                            {/*<Typography.Title level={3}>{Tender.actionPlanForYourBusiness}</Typography.Title>*/}
                                            {/*<b>{Tender.alignStrengthsWithRequirements}:</b>*/}
                                            {/*<ul>*/}
                                            {/*    <li><span>{tender?.decisionIntelligence?.decision_intelligence?.action_plan?.align_strengths}</span></li>*/}
                                            {/*</ul>*/}
                                            {/*<b>{Tender.optimizeProposalDocumentation}:</b>*/}
                                            {/*<ul>*/}
                                            {/*    <li><span>{tender?.decisionIntelligence?.decision_intelligence?.action_plan?.optimize_proposal}</span></li>*/}
                                            {/*</ul>*/}
                                            {/*<b>{Tender.confirmSAMRegistration}:</b>*/}
                                            {/*<ul>*/}
                                            {/*    <li><span>{tender?.decisionIntelligence?.decision_intelligence?.action_plan?.confirm_sam_registration}</span></li>*/}
                                            {/*</ul>*/}
                                            {/*<b>{Tender.refinePricingStrategy}:</b>*/}
                                            {/*<ul>*/}
                                            {/*    <li><span>{tender?.decisionIntelligence?.decision_intelligence?.action_plan?.refine_pricing_strategy}</span></li>*/}
                                            {/*</ul>*/}
                                            {/*<b>{Tender.logisticsPlanning}:</b>*/}
                                            {/*<ul>*/}
                                            {/*    <li><span>{tender?.decisionIntelligence?.decision_intelligence?.action_plan?.logistics_planning}</span></li>*/}
                                            {/*</ul>*/}
                                            {/*<b>{Tender.proactiveCommunication}:</b>*/}
                                            {/*<ul>*/}
                                            {/*    <li><span>{tender?.decisionIntelligence?.decision_intelligence?.action_plan?.proactive_measures}</span></li>*/}
                                            {/*</ul>*/}
                                        </Space>
                                        </>
                                    }
                                </Tabs.TabPane>
                                <Tabs.TabPane key="proposal" tab={Tender.proposal} icon={<FaFileAlt />}>
                                    {
                                        (_.isEmpty(tender?.proposal)) ?
                                            <>
                                                <Alert
                                                    message={Tender.missingDocumentTitle}
                                                    description={Tender.missingDocumentProposalDescription}
                                                    type="info"
                                                    showIcon
                                                />
                                            </> :
                                            <>
                                            <Space direction={'vertical'} size={10}>
                                                <Button type={'text'} loading={pdfProposalLoading} className={'button-white'} icon={<FaArrowCircleRight size={16} />} onClick={async () => {
                                                    if (tender) {
                                                        setPdfProposalLoading(true);
                                                        if (_.isEmpty(tender.proposal)) await Proposal(await decisionIntelligence(tender.id));
                                                        else await Proposal(tender);
                                                        setPdfProposalLoading(false);
                                                    }
                                                }}>{Tender.proposalDOC}</Button>
                                                <Space direction={'vertical'} size={2}>
                                                    <Typography.Title level={3}>{Tender.submissionMethodFormat}</Typography.Title>
                                                    <ul>
                                                        <li>{Tender.method + ": " + tender?.proposal?.Proposal_Template?.Submission_Method_Format?.Method}</li>
                                                        <li>{Tender.format + ": " + tender?.proposal?.Proposal_Template?.Submission_Method_Format?.Format}</li>
                                                    </ul>
                                                    <Divider/>
                                                    <Typography.Title level={3}>{Tender.evaluationAwardCriteria}</Typography.Title>
                                                    <ul>
                                                        <li>{Tender.evaluation + ": " + tender?.proposal?.Proposal_Template?.Evaluation_Award_Criteria?.Summary?.Evaluation_Method}</li>
                                                        <li>{Tender.scoring + ": " + tender?.proposal?.Proposal_Template?.Evaluation_Award_Criteria?.Summary?.Scoring_Methodology}</li>
                                                        <li>{Tender.awardTimeline + ": " + tender?.proposal?.Proposal_Template?.Evaluation_Award_Criteria?.Summary?.Award_Timeline}</li>
                                                    </ul>
                                                    <Divider />
                                                    <Typography.Title level={3}>{Tender.instructionsForUse}</Typography.Title>
                                                    <ul>
                                                        {
                                                            tender?.proposal?.Proposal_Template?.Instructions_for_Use?.map((item: string) => <li>{item}</li>)
                                                        }
                                                    </ul>
                                                </Space>
                                            </Space>
                                            </>
                                    }
                                </Tabs.TabPane>
                            </Tabs>
                        </div>
                    </Col>
                    {
                        showAskBox && (
                            <Col xs={24} sm={24} lg={24} xl={4}>
                                <Affix offsetTop={10}>
                                <div className={'ask'}>
                                    { tenderId && <Ask source={source} tenderId={tenderId} askId={askId} messages={messages} loadingMessages={loadingMessages} askHistorySearchReset={askHistorySearchReset} setAskId={setAskId} loadingAsk={loadingAsk} setShowAskBox={setShowAskBox} setLoadingAsk={setLoadingAsk} getAsks={getAsks} getMessages={getMessages} setAskHistorySearchReset={setAskHistorySearchReset} /> }
                                </div>
                                </Affix>
                            </Col>
                        )
                    }
                </Row>
            </Space>
        </div>
    );
}